/**
 * Line Item Table Component
 * BuilderBel Construction Project Management System
 *
 * Displays estimate line items in a table with inline editing support.
 * Field names match backend EstimateLineItem model exactly.
 */

import React, { useState, useMemo } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import type { EstimateLineItem } from '../../types/estimate';

export interface LineItemTableProps {
  lineItems: EstimateLineItem[];
  onEdit: (lineItem: EstimateLineItem) => void;
  onDelete: (lineItem: EstimateLineItem) => void;
  onInlineEdit?: (lineItemId: string, field: string, value: any) => void;
  groupBy?: 'category' | 'none';
  isEditable?: boolean;
  isLoading?: boolean;
}

interface GroupedItems {
  key: string;
  label: string;
  items: EstimateLineItem[];
  totals: {
    lineTotal: number;
    laborTotal: number;
    materialTotal: number;
    equipmentTotal: number;
  };
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const LineItemRow: React.FC<{
  lineItem: EstimateLineItem;
  onEdit: (lineItem: EstimateLineItem) => void;
  onDelete: (lineItem: EstimateLineItem) => void;
  isEditable?: boolean;
}> = ({ lineItem, onEdit, onDelete, isEditable = true }) => {
  const totalUnitCost =
    (lineItem.laborUnitCost || 0) +
    (lineItem.materialUnitCost || 0) +
    (lineItem.equipmentUnitCost || 0) +
    (lineItem.subcontractorUnitCost || 0) +
    (lineItem.otherUnitCost || 0);

  return (
    <tr className="hover:bg-gray-50 group">
      {isEditable && (
        <td className="w-8 px-2 py-3">
          <GripVertical className="h-4 w-4 text-gray-300 cursor-grab opacity-0 group-hover:opacity-100" />
        </td>
      )}
      <td className="px-4 py-3 text-sm">
        <span className="font-medium text-gray-900">{lineItem.costCode || '-'}</span>
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="max-w-xs truncate text-gray-700">{lineItem.description}</div>
        {lineItem.notes && <div className="text-xs text-gray-400 truncate">{lineItem.notes}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{lineItem.category}</td>
      <td className="px-4 py-3 text-sm text-right text-gray-700">
        {formatNumber(lineItem.quantity)} {lineItem.unit}
      </td>
      <td className="px-4 py-3 text-sm text-right text-gray-700">
        {formatCurrency(totalUnitCost)}
      </td>
      <td className="px-4 py-3 text-sm text-right text-gray-700">
        {formatCurrency(lineItem.laborTotal)}
      </td>
      <td className="px-4 py-3 text-sm text-right text-gray-700">
        {formatCurrency(lineItem.materialTotal)}
      </td>
      <td className="px-4 py-3 text-sm text-right text-gray-700">
        {formatCurrency(lineItem.equipmentTotal)}
      </td>
      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
        {formatCurrency(lineItem.lineTotal)}
      </td>
      {isEditable && (
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onEdit(lineItem)}
              className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700"
              title="Edit"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(lineItem)}
              className="p-1.5 rounded-lg hover:bg-red-100 text-gray-500 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

const GroupHeader: React.FC<{
  group: GroupedItems;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ group, isExpanded, onToggle }) => {
  return (
    <tr className="bg-gray-100 cursor-pointer hover:bg-gray-150" onClick={onToggle}>
      <td colSpan={11} className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            <span className="font-medium text-gray-900">{group.label}</span>
            <span className="text-sm text-gray-500">({group.items.length} items)</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span className="text-gray-500">
              Labor: <span className="font-medium">{formatCurrency(group.totals.laborTotal)}</span>
            </span>
            <span className="text-gray-500">
              Material:{' '}
              <span className="font-medium">{formatCurrency(group.totals.materialTotal)}</span>
            </span>
            <span className="text-gray-500">
              Equipment:{' '}
              <span className="font-medium">{formatCurrency(group.totals.equipmentTotal)}</span>
            </span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(group.totals.lineTotal)}
            </span>
          </div>
        </div>
      </td>
    </tr>
  );
};

const LineItemTable: React.FC<LineItemTableProps> = ({
  lineItems,
  onEdit,
  onDelete,
  groupBy = 'none',
  isEditable = true,
  isLoading = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group items
  const groupedData = useMemo<GroupedItems[]>(() => {
    if (groupBy === 'none') {
      return [
        {
          key: 'all',
          label: 'All Items',
          items: lineItems,
          totals: {
            lineTotal: lineItems.reduce((sum, item) => sum + (item.lineTotal || 0), 0),
            laborTotal: lineItems.reduce((sum, item) => sum + (item.laborTotal || 0), 0),
            materialTotal: lineItems.reduce((sum, item) => sum + (item.materialTotal || 0), 0),
            equipmentTotal: lineItems.reduce((sum, item) => sum + (item.equipmentTotal || 0), 0),
          },
        },
      ];
    }

    const groups: Record<string, EstimateLineItem[]> = {};
    lineItems.forEach(item => {
      const key = item.category || 'Uncategorized';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    return Object.entries(groups)
      .map(([key, items]) => ({
        key,
        label: key,
        items,
        totals: {
          lineTotal: items.reduce((sum, item) => sum + (item.lineTotal || 0), 0),
          laborTotal: items.reduce((sum, item) => sum + (item.laborTotal || 0), 0),
          materialTotal: items.reduce((sum, item) => sum + (item.materialTotal || 0), 0),
          equipmentTotal: items.reduce((sum, item) => sum + (item.equipmentTotal || 0), 0),
        },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lineItems, groupBy]);

  // Initialize all groups as expanded
  React.useEffect(() => {
    setExpandedGroups(new Set(groupedData.map(g => g.key)));
  }, [groupBy]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const totals = useMemo(() => {
    return {
      lineTotal: lineItems.reduce((sum, item) => sum + (item.lineTotal || 0), 0),
      laborTotal: lineItems.reduce((sum, item) => sum + (item.laborTotal || 0), 0),
      materialTotal: lineItems.reduce((sum, item) => sum + (item.materialTotal || 0), 0),
      equipmentTotal: lineItems.reduce((sum, item) => sum + (item.equipmentTotal || 0), 0),
    };
  }, [lineItems]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="animate-pulse p-6">
          <div className="h-8 bg-gray-200 rounded mb-4" />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded mb-2" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {isEditable && <th className="w-8 px-2 py-3" />}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Cost Code
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Unit Cost
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Labor
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Material
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Equipment
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Total
              </th>
              {isEditable && (
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groupBy === 'none'
              ? lineItems.map(lineItem => (
                  <LineItemRow
                    key={lineItem._id}
                    lineItem={lineItem}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    isEditable={isEditable}
                  />
                ))
              : groupedData.map(group => (
                  <React.Fragment key={group.key}>
                    <GroupHeader
                      group={group}
                      isExpanded={expandedGroups.has(group.key)}
                      onToggle={() => toggleGroup(group.key)}
                    />
                    {expandedGroups.has(group.key) &&
                      group.items.map(lineItem => (
                        <LineItemRow
                          key={lineItem._id}
                          lineItem={lineItem}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          isEditable={isEditable}
                        />
                      ))}
                  </React.Fragment>
                ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              {isEditable && <td className="px-2 py-4" />}
              <td colSpan={5} className="px-4 py-4 text-sm font-semibold text-gray-900">
                Total ({lineItems.length} items)
              </td>
              <td className="px-4 py-4 text-sm text-right font-semibold text-gray-900">
                {formatCurrency(totals.laborTotal)}
              </td>
              <td className="px-4 py-4 text-sm text-right font-semibold text-gray-900">
                {formatCurrency(totals.materialTotal)}
              </td>
              <td className="px-4 py-4 text-sm text-right font-semibold text-gray-900">
                {formatCurrency(totals.equipmentTotal)}
              </td>
              <td className="px-4 py-4 text-sm text-right font-bold text-gray-900">
                {formatCurrency(totals.lineTotal)}
              </td>
              {isEditable && <td className="px-4 py-4" />}
            </tr>
          </tfoot>
        </table>
      </div>

      {lineItems.length === 0 && (
        <div className="p-12 text-center">
          <p className="text-gray-500">
            No line items yet. Add your first line item to get started.
          </p>
        </div>
      )}
    </div>
  );
};

export default LineItemTable;
